import { useTranslation } from 'react-i18next';
import { CheckCircleIcon, XCircleIcon } from '@phosphor-icons/react';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { cn } from '@/lib/utils';
import type { PasswordComplexityPolicy } from '@/types/admin-settings';

interface PasswordRequirementsProps {
    password: string;
    policy: PasswordComplexityPolicy;
}

interface Requirement {
    label: string;
    met: boolean;
}

export function PasswordRequirements({
    password,
    policy,
}: PasswordRequirementsProps) {
    const { t } = useTranslation(I18nNamespace.SETTINGS);

    const requirements: Requirement[] = [
        {
            label: t('members.minLength', { count: policy.minLength }),
            met: password.length >= policy.minLength,
        },
        ...(policy.hasUppercase
            ? [
                  {
                      label: t('members.requireUppercase'),
                      met: /[A-Z]/.test(password),
                  },
              ]
            : []),
        ...(policy.hasLowercase
            ? [
                  {
                      label: t('members.requireLowercase'),
                      met: /[a-z]/.test(password),
                  },
              ]
            : []),
        ...(policy.hasNumber
            ? [
                  {
                      label: t('members.requireNumber'),
                      met: /\d/.test(password),
                  },
              ]
            : []),
        ...(policy.hasSymbol
            ? [
                  {
                      label: t('members.requireSymbol'),
                      met: /[^A-Za-z0-9]/.test(password),
                  },
              ]
            : []),
    ];

    return (
        <div className="flex flex-col gap-1">
            <span className="text-xs text-grey-400">
                {t('members.passwordRequirements')}
            </span>
            {requirements.map((req) => (
                <div key={req.label} className="flex items-center gap-1.5">
                    {req.met ? (
                        <CheckCircleIcon className="size-3.5 text-green-bright" />
                    ) : (
                        <XCircleIcon className="size-3.5 text-danger-200" />
                    )}
                    <span
                        className={cn(
                            'text-xs',
                            req.met ? 'text-green-bright' : 'text-danger-200',
                        )}
                    >
                        {req.label}
                    </span>
                </div>
            ))}
        </div>
    );
}
